import React, { useState, useEffect } from 'react';
import { Save, Edit3, RotateCcw, CheckCircle, AlertCircle, Search, MapPin, Award } from 'lucide-react';
import { UAD_STANDARD_VALUES, mapToUADFormat, validateUADCompliance, UAD_VERSIONS } from '../utils/uadMapping';

const EditablePropertyFields = ({ 
  propertyData, 
  onPropertyUpdate, 
  currentAddress,
  isLoading = false,
  onFindComparables = null,
  onNavigateToComparables = null
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    yearBuilt: '',
    stories: '',
    totalRooms: '',
    propertyType: '',
    lotSize: '',
    notes: '',
    maxComparables: '20',
    // NEW UAD FIELDS - Added without affecting existing fields
    condition: '',
    quality: '',
    view: '',
    functionalUtility: '',
    occupancy: '',
    propertyRights: '',
    designStyle: '',
    heatingCooling: '',
    energyEfficient: '',
    basement: '',
    garage: '',
    pool: '',
    fireplace: ''
  });
  const [originalFields, setOriginalFields] = useState({});
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'success', 'error'
  const [validationErrors, setValidationErrors] = useState({});
  const [isSearchingComparables, setIsSearchingComparables] = useState(false);

  // NEW UAD STATE - Added without affecting existing functionality
  const [showUADFields, setShowUADFields] = useState(false);
  const [uadCompliance, setUadCompliance] = useState(null);
  const [uadVersion, setUadVersion] = useState(UAD_VERSIONS.UAD_36);

  // NEW UAD COMPLIANCE CHECK FUNCTION - Added without affecting existing code
  const checkUADCompliance = () => {
    try {
      const uadData = mapToUADFormat(editedFields, uadVersion);
      const compliance = validateUADCompliance(uadData, uadVersion);
      setUadCompliance(compliance);
      return compliance;
    } catch (error) {
      console.error('UAD compliance check error:', error);
      return null;
    }
  };

  // Initialize fields from property data
  useEffect(() => {
    if (propertyData) {
      const fields = {
        bedrooms: propertyData.bedrooms || propertyData.BedroomsTotal || '',
        bathrooms: propertyData.bathrooms || propertyData.BathroomsDecimal || propertyData.BathroomsTotalInteger || '',
        sqft: propertyData.sqft || propertyData.LivingArea || '',
        yearBuilt: propertyData.yearBuilt || propertyData.YearBuilt || '',
        stories: propertyData.stories || propertyData.StoriesTotal || '',
        totalRooms: propertyData.totalRooms || propertyData.RoomsTotal || '',
        propertyType: propertyData.propertyType || propertyData.PropertyType || '',
        lotSize: propertyData.lotSize || propertyData.LotSizeAcres || '',
        notes: propertyData.notes || '',
        // Preserve existing maxComparables value if it exists, otherwise default to 20
        maxComparables: editedFields.maxComparables || propertyData.maxComparables || '20',

        // NEW UAD FIELDS INITIALIZATION - Added without affecting existing fields
        condition: propertyData.condition || '',
        quality: propertyData.quality || '',
        view: propertyData.view || '',
        functionalUtility: propertyData.functionalUtility || '',
        occupancy: propertyData.occupancy || '',
        propertyRights: propertyData.propertyRights || '',
        designStyle: propertyData.designStyle || '',
        heatingCooling: propertyData.heatingCooling || '',
        energyEfficient: propertyData.energyEfficient || '',
        basement: propertyData.basement || '',
        garage: propertyData.garage || '',
        pool: propertyData.pool || '',
        fireplace: propertyData.fireplace || ''
      };
      setEditedFields(prev => ({
        ...fields,
        // Always preserve the current maxComparables value
        maxComparables: prev.maxComparables || fields.maxComparables
      }));
      setOriginalFields(prev => ({
        ...fields,
        // Also preserve in originalFields to prevent revert
        maxComparables: prev.maxComparables || fields.maxComparables
      }));

      // Check UAD compliance when data changes
      setTimeout(() => checkUADCompliance(), 100);
    }
  }, [propertyData, uadVersion]);

  // NEW UAD COMPLIANCE CHECK ON FIELD CHANGES - Added without affecting existing code
  useEffect(() => {
    if (showUADFields && editedFields.bedrooms) {
      const timeoutId = setTimeout(() => {
        checkUADCompliance();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [editedFields, showUADFields, uadVersion]);

  // Validation function
  const validateFields = () => {
    const errors = {};
    
    if (editedFields.bedrooms && (parseInt(editedFields.bedrooms) < 0 || parseInt(editedFields.bedrooms) > 20)) {
      errors.bedrooms = 'Bedrooms must be between 0 and 20';
    }
    
    if (editedFields.bathrooms && (parseFloat(editedFields.bathrooms) < 0 || parseFloat(editedFields.bathrooms) > 20)) {
      errors.bathrooms = 'Bathrooms must be between 0 and 20';
    }
    
    if (editedFields.sqft && (parseInt(editedFields.sqft) < 100 || parseInt(editedFields.sqft) > 50000)) {
      errors.sqft = 'Square footage must be between 100 and 50,000';
    }
    
    if (editedFields.yearBuilt && (parseInt(editedFields.yearBuilt) < 1800 || parseInt(editedFields.yearBuilt) > new Date().getFullYear() + 5)) {
      errors.yearBuilt = `Year built must be between 1800 and ${new Date().getFullYear() + 5}`;
    }
    
    if (editedFields.stories && (parseFloat(editedFields.stories) < 0 || parseFloat(editedFields.stories) > 10)) {
      errors.stories = 'Stories must be between 0 and 10';
    }
    
    if (editedFields.totalRooms && (parseInt(editedFields.totalRooms) < 0 || parseInt(editedFields.totalRooms) > 50)) {
      errors.totalRooms = 'Total rooms must be between 0 and 50';
    }
    
    if (editedFields.searchRadius && (parseFloat(editedFields.searchRadius) < 0.5 || parseFloat(editedFields.searchRadius) > 50)) {
      errors.searchRadius = 'Search radius must be between 0.5 and 50 miles';
    }
    
    if (editedFields.maxComparables && (parseInt(editedFields.maxComparables) < 1 || parseInt(editedFields.maxComparables) > 100)) {
      errors.maxComparables = 'Number of comparables must be between 1 and 100';
    }

    return errors;
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setEditedFields(prev => {
      const newFields = { ...prev, [field]: value };
      
      // Trigger comparable search when any field changes
      if (onFindComparables && currentAddress) {
        const editedPropertyForSearch = {
          address: currentAddress,
          bedrooms: newFields.bedrooms ? parseInt(newFields.bedrooms) : null,
          bathrooms: newFields.bathrooms ? parseFloat(newFields.bathrooms) : null,
          sqft: newFields.sqft ? parseInt(newFields.sqft) : null,
          yearBuilt: newFields.yearBuilt ? parseInt(newFields.yearBuilt) : null,
          stories: newFields.stories ? parseFloat(newFields.stories) : null,
          totalRooms: newFields.totalRooms ? parseInt(newFields.totalRooms) : null,
          propertyType: newFields.propertyType || null,
          lotSize: newFields.lotSize ? parseFloat(newFields.lotSize) : null,
          notes: newFields.notes || null
        };
        
        console.log('🔄 Field changed, triggering comparable search with:', editedPropertyForSearch);
        console.log('🔄 Field that changed:', field, 'New value:', value);
        
        // Debounce the search to avoid too many API calls
        clearTimeout(window.comparableSearchTimeout);
        window.comparableSearchTimeout = setTimeout(() => {
          console.log('🚀 Executing debounced comparable search');
          onFindComparables(editedPropertyForSearch, {
            searchRadius: 3,
            maxResults: parseInt(newFields.maxComparables) || 20,
            tolerance: {
              bedrooms: 1,
              bathrooms: 0.5,
              sqft: 0.25,
              yearBuilt: 10,
              stories: 0.5
            }
          });
        }, 500); // Wait 500ms after user stops typing
      }
      
      return newFields;
    });
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle save
  const handleSave = async () => {
    const errors = validateFields();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (!currentAddress) {
      setValidationErrors({ general: 'Property address is required' });
      return;
    }

    setSaveStatus('saving');
    setValidationErrors({});

    try {
      // Call the onPropertyUpdate function with the updated fields (exclude maxComparables from backend save)
      const { maxComparables, ...propertyFieldsForBackend } = editedFields;
      
      await onPropertyUpdate({
        address: currentAddress,
        ...propertyFieldsForBackend
      });

      setSaveStatus('success');
      setOriginalFields({ ...editedFields }); // Keep maxComparables in local state
      setIsEditing(false);

      // Clear success status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('error');
      setValidationErrors({ general: error.message || 'Failed to save property updates' });
      
      // Clear error status after 5 seconds
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setEditedFields({ ...originalFields });
    setIsEditing(false);
    setValidationErrors({});
    setSaveStatus(null);
  };

  // Handle edit toggle
  const handleEditToggle = () => {
    if (isEditing) {
      handleCancel();
    } else {
      setIsEditing(true);
    }
  };

  // Check if there are changes
  const hasChanges = () => {
    return Object.keys(editedFields).some(key => 
      editedFields[key] !== originalFields[key]
    );
  };

  // Handle find comparables based on edited property details
  const handleFindComparables = async () => {
    if (!currentAddress) {
      setValidationErrors({ general: 'Property address is required to find comparables' });
      return;
    }

    // Validate fields before searching
    const errors = validateFields();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsSearchingComparables(true);
    setValidationErrors({});

    try {
      // Create subject property object from edited fields
      const subjectPropertyForSearch = {
        address: currentAddress,
        bedrooms: editedFields.bedrooms ? parseInt(editedFields.bedrooms) : null,
        bathrooms: editedFields.bathrooms ? parseFloat(editedFields.bathrooms) : null,
        sqft: editedFields.sqft ? parseInt(editedFields.sqft) : null,
        yearBuilt: editedFields.yearBuilt ? parseInt(editedFields.yearBuilt) : null,
        stories: editedFields.stories ? parseFloat(editedFields.stories) : null,
        totalRooms: editedFields.totalRooms ? parseInt(editedFields.totalRooms) : null,
        propertyType: editedFields.propertyType || null,
        lotSize: editedFields.lotSize ? parseFloat(editedFields.lotSize) : null,
        notes: editedFields.notes || null
      };

      console.log('🔍 Finding comparables for edited property:', subjectPropertyForSearch);

      // Call the parent component's comparable search function with radius
      if (onFindComparables) {
        const searchCriteria = {
          searchRadius: parseFloat(editedFields.searchRadius) || 3,
          maxResults: parseInt(editedFields.maxComparables) || 20,
          tolerance: {
            bedrooms: 1,
            bathrooms: 0.5,
            sqft: 0.25,
            yearBuilt: 10,
            stories: 0.5
          }
        };
        
        await onFindComparables(subjectPropertyForSearch, searchCriteria);
        
        // Navigate to comparables tab if function provided
        if (onNavigateToComparables) {
          onNavigateToComparables();
        }
      } else {
        console.warn('No onFindComparables function provided');
      }

    } catch (error) {
      console.error('Find comparables error:', error);
      setValidationErrors({ 
        general: error.message || 'Failed to find comparable properties' 
      });
    } finally {
      setIsSearchingComparables(false);
    }
  };

  // Check if we can search for comparables
  const canFindComparables = () => {
    return currentAddress && (
      editedFields.bedrooms || 
      editedFields.bathrooms || 
      editedFields.sqft || 
      editedFields.yearBuilt
    );
  };

  if (!propertyData && !currentAddress) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="text-center text-gray-400">
          <Edit3 className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-lg font-medium mb-2">No Property Selected</h3>
          <p className="text-sm">Search for a property to edit its details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">Subject Property Details</h3>
          <p className="text-sm text-gray-400">
            {currentAddress || 'Edit property characteristics after visiting the property'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saveStatus && (
            <div className={`flex items-center gap-1 text-sm ${
              saveStatus === 'success' ? 'text-green-400' :
              saveStatus === 'error' ? 'text-red-400' :
              'text-yellow-400'
            }`}>
              {saveStatus === 'saving' && (
                <>
                  <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              )}
              {saveStatus === 'success' && (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Saved!
                </>
              )}
              {saveStatus === 'error' && (
                <>
                  <AlertCircle className="w-4 h-4" />
                  Error
                </>
              )}
            </div>
          )}

          {/* NEW UAD TOGGLE BUTTON - Added without affecting existing buttons */}
          <button
            onClick={() => setShowUADFields(!showUADFields)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              showUADFields
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
            }`}
          >
            <Award className="w-4 h-4" />
            UAD Fields
          </button>

          <button
            onClick={handleEditToggle}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isEditing
                ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            disabled={isLoading}
          >
            <Edit3 className="w-4 h-4" />
            {isEditing ? 'Cancel Edit' : 'Edit Fields'}
          </button>
        </div>
      </div>

      {/* General validation error */}
      {validationErrors.general && (
        <div className="mb-4 p-3 bg-red-900 border border-red-700 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{validationErrors.general}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Bedrooms */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Bedrooms
          </label>
          <input
            type="number"
            value={editedFields.bedrooms}
            onChange={(e) => handleFieldChange('bedrooms', e.target.value)}
            disabled={!isEditing}
            min="0"
            max="20"
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            } ${validationErrors.bedrooms ? 'border-red-500' : ''}`}
            placeholder="e.g., 4"
          />
          {validationErrors.bedrooms && (
            <p className="text-red-400 text-xs mt-1">{validationErrors.bedrooms}</p>
          )}
        </div>

        {/* Bathrooms */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Bathrooms
          </label>
          <input
            type="number"
            step="0.5"
            value={editedFields.bathrooms}
            onChange={(e) => handleFieldChange('bathrooms', e.target.value)}
            disabled={!isEditing}
            min="0"
            max="20"
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            } ${validationErrors.bathrooms ? 'border-red-500' : ''}`}
            placeholder="e.g., 2.5"
          />
          {validationErrors.bathrooms && (
            <p className="text-red-400 text-xs mt-1">{validationErrors.bathrooms}</p>
          )}
        </div>

        {/* Square Footage */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Square Footage
          </label>
          <input
            type="number"
            value={editedFields.sqft}
            onChange={(e) => handleFieldChange('sqft', e.target.value)}
            disabled={!isEditing}
            min="100"
            max="50000"
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            } ${validationErrors.sqft ? 'border-red-500' : ''}`}
            placeholder="e.g., 2100"
          />
          {validationErrors.sqft && (
            <p className="text-red-400 text-xs mt-1">{validationErrors.sqft}</p>
          )}
        </div>

        {/* Year Built */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Year Built
          </label>
          <input
            type="number"
            value={editedFields.yearBuilt}
            onChange={(e) => handleFieldChange('yearBuilt', e.target.value)}
            disabled={!isEditing}
            min="1800"
            max={new Date().getFullYear() + 5}
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            } ${validationErrors.yearBuilt ? 'border-red-500' : ''}`}
            placeholder="e.g., 2015"
          />
          {validationErrors.yearBuilt && (
            <p className="text-red-400 text-xs mt-1">{validationErrors.yearBuilt}</p>
          )}
        </div>

        {/* Stories */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Stories
          </label>
          <input
            type="number"
            step="0.5"
            value={editedFields.stories}
            onChange={(e) => handleFieldChange('stories', e.target.value)}
            disabled={!isEditing}
            min="0"
            max="10"
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            } ${validationErrors.stories ? 'border-red-500' : ''}`}
            placeholder="e.g., 2"
          />
          {validationErrors.stories && (
            <p className="text-red-400 text-xs mt-1">{validationErrors.stories}</p>
          )}
        </div>

        {/* Total Rooms */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Total Rooms
          </label>
          <input
            type="number"
            value={editedFields.totalRooms}
            onChange={(e) => handleFieldChange('totalRooms', e.target.value)}
            disabled={!isEditing}
            min="0"
            max="50"
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            } ${validationErrors.totalRooms ? 'border-red-500' : ''}`}
            placeholder="e.g., 8"
          />
          {validationErrors.totalRooms && (
            <p className="text-red-400 text-xs mt-1">{validationErrors.totalRooms}</p>
          )}
        </div>

        {/* Property Type */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Property Type
          </label>
          <select
            value={editedFields.propertyType}
            onChange={(e) => handleFieldChange('propertyType', e.target.value)}
            disabled={!isEditing}
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            <option value="">Select type...</option>
            <option value="Single Family">Single Family</option>
            <option value="Townhouse">Townhouse</option>
            <option value="Condo">Condo</option>
            <option value="Multi-Family">Multi-Family</option>
            <option value="Land">Land</option>
            <option value="Commercial">Commercial</option>
          </select>
        </div>

        {/* Lot Size */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Lot Size (acres)
          </label>
          <input
            type="number"
            step="0.01"
            value={editedFields.lotSize}
            onChange={(e) => handleFieldChange('lotSize', e.target.value)}
            disabled={!isEditing}
            min="0"
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            }`}
            placeholder="e.g., 0.25"
          />
        </div>

        {/* Search Radius */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Search Radius (miles)
          </label>
          <select
            value={editedFields.searchRadius}
            onChange={(e) => handleFieldChange('searchRadius', e.target.value)}
            disabled={!isEditing}
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            } ${validationErrors.searchRadius ? 'border-red-500' : ''}`}
          >
            <option value="0.5">0.5 miles</option>
            <option value="1">1 mile</option>
            <option value="2">2 miles</option>
            <option value="3">3 miles</option>
            <option value="5">5 miles</option>
            <option value="10">10 miles</option>
            <option value="15">15 miles</option>
            <option value="20">20 miles</option>
            <option value="25">25 miles</option>
            <option value="50">50 miles</option>
          </select>
          {validationErrors.searchRadius && (
            <p className="text-red-400 text-xs mt-1">{validationErrors.searchRadius}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Radius for finding comparable properties
          </p>
        </div>

        {/* Max Comparables */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Number of Comparables
          </label>
          <input
            type="number"
            value={editedFields.maxComparables}
            onChange={(e) => handleFieldChange('maxComparables', e.target.value)}
            disabled={!isEditing}
            min="1"
            max="100"
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
              !isEditing ? 'opacity-60 cursor-not-allowed' : ''
            } ${validationErrors.maxComparables ? 'border-red-500' : ''}`}
            placeholder="e.g., 20"
          />
          {validationErrors.maxComparables && (
            <p className="text-red-400 text-xs mt-1">{validationErrors.maxComparables}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Maximum number of top matching comparables to display
          </p>
        </div>
      </div>

      {/* NEW UAD COMPLIANCE PANEL - Added without affecting existing sections */}
      {showUADFields && uadCompliance && (
        <div className="mt-6 bg-gray-800 border border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-green-400" />
              <h4 className="text-lg font-semibold text-white">UAD {uadVersion} Compliance</h4>
              <select
                value={uadVersion}
                onChange={(e) => setUadVersion(e.target.value)}
                className="ml-2 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              >
                <option value={UAD_VERSIONS.UAD_26}>UAD 2.6</option>
                <option value={UAD_VERSIONS.UAD_36}>UAD 3.6</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              {uadCompliance.isValid ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <AlertCircle className="w-5 h-5 text-yellow-400" />
              )}
              <span className="text-sm font-medium">
                {uadCompliance.compliancePercentage}% Complete
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-700 p-3 rounded text-center">
              <div className="text-lg font-bold text-green-400">
                {uadCompliance.compliance.requiredFieldsPresent}
              </div>
              <div className="text-sm text-gray-300">Required Fields</div>
            </div>
            <div className="bg-gray-700 p-3 rounded text-center">
              <div className="text-lg font-bold text-red-400">
                {uadCompliance.errors.length}
              </div>
              <div className="text-sm text-gray-300">Errors</div>
            </div>
            <div className="bg-gray-700 p-3 rounded text-center">
              <div className="text-lg font-bold text-yellow-400">
                {uadCompliance.warnings.length}
              </div>
              <div className="text-sm text-gray-300">Warnings</div>
            </div>
          </div>

          {(uadCompliance.errors.length > 0 || uadCompliance.warnings.length > 0) && (
            <div className="space-y-2">
              {uadCompliance.errors.length > 0 && (
                <div className="p-3 bg-red-900 border border-red-700 rounded">
                  <h6 className="text-sm font-medium text-red-200 mb-2">Required Fields Missing:</h6>
                  <ul className="text-xs text-red-300 space-y-1">
                    {uadCompliance.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {uadCompliance.warnings.length > 0 && (
                <div className="p-3 bg-yellow-900 border border-yellow-700 rounded">
                  <h6 className="text-sm font-medium text-yellow-200 mb-2">Recommendations:</h6>
                  <ul className="text-xs text-yellow-300 space-y-1">
                    {uadCompliance.warnings.map((warning, index) => (
                      <li key={index}>• {warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* NEW UAD FIELDS SECTION - Added without affecting existing fields */}
      {showUADFields && (
        <div className="mt-6 bg-gray-800 border border-gray-700 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-white mb-4">UAD Standard Fields</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

            {/* Property Rights */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Property Rights <span className="text-red-400">*</span>
              </label>
              <select
                value={editedFields.propertyRights}
                onChange={(e) => handleFieldChange('propertyRights', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select...</option>
                {UAD_STANDARD_VALUES.propertyRights.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            {/* Occupancy */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Occupancy <span className="text-red-400">*</span>
              </label>
              <select
                value={editedFields.occupancy}
                onChange={(e) => handleFieldChange('occupancy', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select...</option>
                {UAD_STANDARD_VALUES.occupancy.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            {/* Condition */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Condition <span className="text-red-400">*</span>
              </label>
              <select
                value={editedFields.condition}
                onChange={(e) => handleFieldChange('condition', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select...</option>
                {UAD_STANDARD_VALUES.condition.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            {/* Quality */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Quality <span className="text-red-400">*</span>
              </label>
              <select
                value={editedFields.quality}
                onChange={(e) => handleFieldChange('quality', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select...</option>
                {UAD_STANDARD_VALUES.quality.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            {/* View */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                View
              </label>
              <select
                value={editedFields.view}
                onChange={(e) => handleFieldChange('view', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select...</option>
                {UAD_STANDARD_VALUES.view.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            {/* Functional Utility */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Functional Utility
              </label>
              <select
                value={editedFields.functionalUtility}
                onChange={(e) => handleFieldChange('functionalUtility', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select...</option>
                {UAD_STANDARD_VALUES.functionalUtility.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            {/* Design/Style */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Design/Style
              </label>
              <input
                type="text"
                value={editedFields.designStyle}
                onChange={(e) => handleFieldChange('designStyle', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                placeholder="e.g., Ranch, Colonial"
              />
            </div>

            {/* Heating/Cooling */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Heating/Cooling
              </label>
              <input
                type="text"
                value={editedFields.heatingCooling}
                onChange={(e) => handleFieldChange('heatingCooling', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                placeholder="e.g., Central Air/Heat"
              />
            </div>

            {/* Energy Efficient Items */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Energy Efficient Items
              </label>
              <input
                type="text"
                value={editedFields.energyEfficient}
                onChange={(e) => handleFieldChange('energyEfficient', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                placeholder="e.g., Solar, Energy Star"
              />
            </div>

            {/* Basement */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Basement
              </label>
              <input
                type="text"
                value={editedFields.basement}
                onChange={(e) => handleFieldChange('basement', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                placeholder="e.g., Full, Partial, None"
              />
            </div>

            {/* Garage */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Garage
              </label>
              <input
                type="text"
                value={editedFields.garage}
                onChange={(e) => handleFieldChange('garage', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                placeholder="e.g., 2-Car Attached"
              />
            </div>

            {/* Pool */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Pool
              </label>
              <select
                value={editedFields.pool}
                onChange={(e) => handleFieldChange('pool', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select...</option>
                <option value="None">None</option>
                <option value="In-Ground">In-Ground</option>
                <option value="Above-Ground">Above-Ground</option>
                <option value="Spa/Hot Tub">Spa/Hot Tub</option>
              </select>
            </div>

            {/* Fireplace */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Fireplace
              </label>
              <input
                type="text"
                value={editedFields.fireplace}
                onChange={(e) => handleFieldChange('fireplace', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 ${
                  !isEditing ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                placeholder="e.g., 1 FP, None"
              />
            </div>

          </div>
          <p className="text-xs text-gray-400 mt-4">
            <span className="text-red-400">*</span> Required for UAD compliance
          </p>
        </div>
      )}

      {/* Notes field */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Notes
        </label>
        <textarea
          value={editedFields.notes}
          onChange={(e) => handleFieldChange('notes', e.target.value)}
          disabled={!isEditing}
          rows={3}
          className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 resize-none ${
            !isEditing ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          placeholder="Add notes about property corrections made after visiting..."
        />
      </div>

      {/* Comparable Search Preview */}
      {canFindComparables() && (
        <div className="mt-4 p-4 bg-blue-900 border border-blue-700 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-blue-300" />
            <h4 className="text-sm font-medium text-blue-200">Live Comparable Search</h4>
            <span className="ml-auto px-2 py-1 bg-green-600 text-green-100 text-xs rounded">
              Auto-updating
            </span>
          </div>
          <p className="text-xs text-blue-400 mb-3">
            Comparables automatically update as you edit property details
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {editedFields.bedrooms && (
              <div className="text-blue-300">
                <span className="text-blue-400">🛏️</span> {editedFields.bedrooms} beds
              </div>
            )}
            {editedFields.bathrooms && (
              <div className="text-blue-300">
                <span className="text-blue-400">🚿</span> {editedFields.bathrooms} baths
              </div>
            )}
            {editedFields.sqft && (
              <div className="text-blue-300">
                <span className="text-blue-400">📐</span> {parseInt(editedFields.sqft).toLocaleString()} sq ft
              </div>
            )}
            {editedFields.yearBuilt && (
              <div className="text-blue-300">
                <span className="text-blue-400">📅</span> Built {editedFields.yearBuilt}
              </div>
            )}
            {editedFields.stories && (
              <div className="text-blue-300">
                <span className="text-blue-400">🏠</span> {editedFields.stories} stories
              </div>
            )}
            {editedFields.propertyType && (
              <div className="text-blue-300">
                <span className="text-blue-400">🏘️</span> {editedFields.propertyType}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-blue-800">
            <p className="text-xs text-blue-400">
              These details will be used to find similar properties with smart similarity scoring
            </p>
            <div className="text-xs text-blue-300 bg-blue-800 px-2 py-1 rounded">
              📊 Top {editedFields.maxComparables || 20} matches
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-700">
        {/* Find Comparables Button - Always visible */}
        <button
          onClick={handleFindComparables}
          disabled={!canFindComparables() || isSearchingComparables || isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSearchingComparables ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              Find Comparables
            </>
          )}
        </button>

        {!canFindComparables() && (
          <p className="text-xs text-gray-400 ml-2">
            Enter property details to find comparables
          </p>
        )}

        {/* Edit mode buttons */}
        {isEditing && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition-colors"
              disabled={saveStatus === 'saving'}
            >
              <RotateCcw className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges() || saveStatus === 'saving'}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-4 h-4" />
              {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Change indicator */}
      {isEditing && hasChanges() && (
        <div className="mt-4 p-3 bg-yellow-900 border border-yellow-700 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-300">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">You have unsaved changes</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditablePropertyFields;